import React, { useCallback, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import {
  ExternalRealPropertyModel,
  RealPropertyModel,
} from '../../../../../../../../typings/api/skymap/rest/v1/project';
import { SkyMapAxiosServiceFactory } from '../../../../../../services/axios/skymap-axios-service-factory';
import { joinIfDefined } from '../../../../../../utils/text/text';
import { useComponentStatus } from '../../../../../hooks/use-component-status';
import { projectStateAdapter } from '../../../../../state/project-state/project-state-adapter';
import { ThemeManager } from '../../../../../themes/theme-manager';
import { Button } from '../../../../button/button';
import { CheckBox } from '../../../../check-box/check-box';
import { Dialog } from '../../../../dialog/dialog';
import { Hyperlink } from '../../../../hyperlink/hyperlink';
import { InfoBox } from '../../../../info-box/info-box';
import { Stack } from '../../../../stack/stack';

type Props = {
  item: ExternalRealPropertyModel;
  onClose: () => void;
  addRealProperty: (property: RealPropertyModel) => void;
};

export const PurchaseRealPropertyDialog = (props: Props) => {
  const { t } = useTranslation();
  const [checkboxChecked, setCheckboxChecked] = useState(false);
  const { setStatus, isLoading } = useComponentStatus();
  const { createRealProperty } = useCreateRealProperty();
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  return (
    <Dialog
      closeButtonRef={closeButtonRef}
      closeIcon={!isLoading}
      closeOnDimmerClick={!isLoading}
      width={500}
      onClose={props.onClose}
    >
      {{
        header: t('onDemand.dialogs.purchase.title', { ns: 'components' }),
        content: (
          <Stack spacing={0.5}>
            <InfoBox bottomMargin={true} color="yellow">
              <Stack spacing={0.4}>
                <span>
                  <Trans
                    components={{ bold: <strong /> }}
                    i18nKey="onDemand.dialogs.purchase.info"
                    ns="components"
                    values={{
                      propertyName: joinIfDefined(' ', [props.item.municipality, props.item.name]),
                    }}
                  />
                </span>
                <span>
                  <Trans
                    components={{
                      hyperlink: (
                        <Hyperlink
                          target="_blank"
                          url="https://www.lantmateriet.se/sv/kartor/vara-karttjanster/Visar-granserna-ratt/"
                        />
                      ),
                    }}
                    i18nKey="onDemand.dialogs.purchase.accuracyText"
                    ns="components"
                  />
                </span>
              </Stack>
            </InfoBox>
            <CheckBox
              checked={checkboxChecked}
              disabled={isLoading}
              onChange={() => setCheckboxChecked(!checkboxChecked)}
            >
              <Trans
                components={{
                  hyperlink: (
                    <Hyperlink
                      target="_blank"
                      url={ThemeManager.instance.getOnDemandPriceListUrl()}
                    />
                  ),
                }}
                i18nKey="onDemand.dialogs.purchase.confirmCheckbox"
                ns="components"
              />
            </CheckBox>
          </Stack>
        ),
        footer: {
          right: (
            <>
              <Button
                color="primary"
                disabled={isLoading || !checkboxChecked}
                loading={isLoading}
                variant="contained"
                onClick={async () => {
                  try {
                    setStatus('loading');
                    const result = await createRealProperty(props.item.externalId);
                    props.addRealProperty(result.data);
                    props.onClose();
                  } catch {
                    setStatus('error');
                  }
                }}
              >
                {t('onDemand.dialogs.purchase.purchasePropertyBoundary', { ns: 'components' })}
              </Button>

              <Button
                disabled={isLoading}
                ref={closeButtonRef}
                variant="text"
                onClick={props.onClose}
              >
                {t('close', { ns: 'common' })}
              </Button>
            </>
          ),
        },
      }}
    </Dialog>
  );
};

const useCreateRealProperty = () => {
  const createRealProperty = useCallback(async (externalId: string) => {
    return await SkyMapAxiosServiceFactory.instance
      .createProjectServiceV1()
      .createProjectRealProperty({
        path: {
          externalId: externalId,
          projectId: projectStateAdapter.getSnapshot().projectId!,
        },
      });
  }, []);

  return { createRealProperty };
};
